import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseSplitBills, buildPath} from './constants';
import {
  IGetGroupDashboardResponse,
  IGetGroupExpensesResponse,
  IJoinGroupResendRequest,
  IJoinGroupStartRequest,
  IJoinGroupStartResponse,
  IJoinGroupVerifyRequest,
  IJoinGroupVerifyResponse,
  IRecordGroupExpenseRequest,
  IRecordGroupExpenseResponse,
  ISettlementRequest,
  IUserDashboardResponse,
} from './entities';

// =========================
// BASE QUERY
// =========================

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;

      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);

      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseSplitBills.baseUrl;

  return reAuth(args, api, extraOptions, baseUrl, baseQuery);
};

// =========================
// API
// =========================

export const splitBillsGroupsApi = createApi({
  reducerPath: EReducersPath.SPLIT_BILLS_GROUPS_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,

  endpoints: build => ({
    // =========================
    // GROUPS
    // =========================

    getGroup: build.query<IGetGroupDashboardResponse, {groupId: string}>({
      query: ({groupId}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.getGroup, {groupId}),
        method: 'GET',
      }),
    }),

    createGroup: build.mutation<
      void,
      {
        name: string;
        emoji: string;
      }
    >({
      query: body => ({
        url: apiBaseSplitBills.endpoints.createGroup,
        method: 'POST',
        body,
      }),
    }),

    // =========================
    // USER DASHBOARD
    // =========================

    getUserDashboard: build.query<
      IUserDashboardResponse,
      {splitUserId: string}
    >({
      query: ({splitUserId}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.getUserDashboard, {
          splitUserId,
        }),
        method: 'GET',
      }),
    }),

    getMyDashboard: build.query<IUserDashboardResponse, void>({
      query: () => ({
        url: apiBaseSplitBills.endpoints.getMyDashboard,
        method: 'GET',
      }),
    }),

    // =========================
    // JOIN GROUP (OTP FLOW)
    // =========================

    joinGroupStart: build.mutation<
      IJoinGroupStartResponse,
      IJoinGroupStartRequest
    >({
      query: ({group_id, ...body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.joinGroupStart, {
          groupId: group_id,
        }),
        method: 'POST',
        body,
      }),
    }),

    joinGroupVerify: build.mutation<
      IJoinGroupVerifyResponse,
      IJoinGroupVerifyRequest
    >({
      query: ({group_id, ...body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.joinGroupVerify, {
          groupId: group_id,
        }),

        method: 'POST',
        body,
      }),
    }),

    joinGroupResend: build.mutation<void, IJoinGroupResendRequest>({
      query: ({group_id, ...body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.joinGroupResend, {
          groupId: group_id,
        }),
        method: 'POST',
        body,
      }),
    }),

    // =========================
    // EXPENSES
    // =========================

    getGroupExpenses: build.query<
      IGetGroupExpensesResponse,
      {groupId: string; limit?: number; lastKey?: string}
    >({
      query: ({groupId, limit = 10, lastKey}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.getGroupExpenses, {groupId}),
        method: 'GET',
        params: {
          limit,
          ...(lastKey ? {offset: lastKey} : {}),
        },
      }),
    }),

    recordExpense: build.mutation<
      IRecordGroupExpenseResponse,
      {groupId: string; body: IRecordGroupExpenseRequest}
    >({
      query: ({groupId, body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.recordExpense, {
          groupId,
        }),
        method: 'POST',
        body,
      }),
    }),

    recordAnonymousExpense: build.mutation<
      IRecordGroupExpenseResponse,
      {groupId: string; body: IRecordGroupExpenseRequest}
    >({
      query: ({groupId, body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.recordAnonymousExpense, {
          groupId,
        }),
        method: 'POST',
        body,
      }),
    }),

    // =========================
    // SETTLEMENTS
    // =========================

    confirmSettlement: build.mutation<
      void,
      {groupId: string; body: ISettlementRequest}
    >({
      query: ({groupId, body}) => ({
        url: buildPath(apiBaseSplitBills.endpoints.confirmSettlement, {
          groupId,
        }),
        method: 'POST',
        body,
      }),
    }),
  }),
});

// =========================
// HOOKS
// =========================

export const {
  useGetGroupQuery,
  useLazyGetGroupQuery,

  useCreateGroupMutation,

  useGetUserDashboardQuery,
  useLazyGetUserDashboardQuery,
  useGetMyDashboardQuery,
  useLazyGetMyDashboardQuery,

  useJoinGroupStartMutation,
  useJoinGroupVerifyMutation,
  useJoinGroupResendMutation,

  useGetGroupExpensesQuery,
  useLazyGetGroupExpensesQuery,

  useRecordExpenseMutation,
  useRecordAnonymousExpenseMutation,

  useConfirmSettlementMutation,
} = splitBillsGroupsApi;
